import { inject } from '@aesop-fables/containr';
import { ISubject, DataCache, ScriniumServices } from '@aesop-fables/scrinium';
import type { IAppStorage } from '@aesop-fables/scrinium';
import { combineLatest, map, Observable } from 'rxjs';
import {
  PricebookBundle,
  AvailablePricebooks,
  PricebookBundleWithUsage,
} from '../../../models/PricebookData';
import { SelectionData } from '../../../api/apis/PricebookApi';
import { derivePricebookSelections } from '../../../helpers/pricebookUtils';
import { PricebookCompartments, pricebookStorageKey } from '..';

export class PricebookOptionsSubject implements ISubject<PricebookBundle[] | undefined> {
  private readonly cache: DataCache<PricebookCompartments>;

  constructor(@inject(ScriniumServices.AppStorage) private readonly storage: IAppStorage) {
    this.cache = this.storage.retrieve<PricebookCompartments>(pricebookStorageKey);
  }

  createObservable(): Observable<PricebookBundle[] | undefined> {
    return this.cache.observe$<PricebookBundle[] | undefined>('pricebookOptions');
  }
}

export class PricebookSelectionsSubject implements ISubject<PricebookBundle[] | undefined> {
  private readonly cache: DataCache<PricebookCompartments>;

  constructor(@inject(ScriniumServices.AppStorage) private readonly storage: IAppStorage) {
    this.cache = this.storage.retrieve<PricebookCompartments>(pricebookStorageKey);
  }

  createObservable(): Observable<PricebookBundle[] | undefined> {
    return combineLatest([
      this.cache.observe$<PricebookBundle[] | undefined>('pricebookOptions'),
      this.cache.observe$<SelectionData[] | undefined>('pricebookSelectionIds'),
    ]).pipe(
      map(([options, selected]) => {
        if (!options || !selected) return undefined;
        return derivePricebookSelections(options, selected);
      }),
    );
  }
}

export class BasePricebookOptionsSubject implements ISubject<PricebookBundle[] | undefined> {
  private readonly cache: DataCache<PricebookCompartments>;

  constructor(@inject(ScriniumServices.AppStorage) private readonly storage: IAppStorage) {
    this.cache = this.storage.retrieve<PricebookCompartments>(pricebookStorageKey);
  }

  createObservable(): Observable<PricebookBundle[] | undefined> {
    return this.cache.observe$<PricebookBundle[] | undefined>('pricebookOptions').pipe(
      map(options =>
        // filter out add-on pricebooks
        options?.filter(option => !option.pricebook.addOn),
      ),
    );
  }
}

export class OrgAvailablePricebooksSubject implements ISubject<AvailablePricebooks | undefined> {
  private readonly cache: DataCache<PricebookCompartments>;

  constructor(@inject(ScriniumServices.AppStorage) private readonly storage: IAppStorage) {
    this.cache = this.storage.retrieve<PricebookCompartments>(pricebookStorageKey);
  }

  createObservable(): Observable<AvailablePricebooks | undefined> {
    return this.cache.observe$<AvailablePricebooks | undefined>('orgAvailablePricebooks');
  }
}

export class TeamAvailablePricebooksSubject implements ISubject<AvailablePricebooks | undefined> {
  private readonly cache: DataCache<PricebookCompartments>;

  constructor(@inject(ScriniumServices.AppStorage) private readonly storage: IAppStorage) {
    this.cache = this.storage.retrieve<PricebookCompartments>(pricebookStorageKey);
  }

  createObservable(): Observable<AvailablePricebooks | undefined> {
    return this.cache.observe$<AvailablePricebooks | undefined>('teamAvailablePricebooks');
  }
}

export class TeamPricebooksInUseSubject implements ISubject<
  PricebookBundleWithUsage[] | undefined
> {
  private readonly cache: DataCache<PricebookCompartments>;

  constructor(@inject(ScriniumServices.AppStorage) private readonly storage: IAppStorage) {
    this.cache = this.storage.retrieve<PricebookCompartments>(pricebookStorageKey);
  }

  createObservable(): Observable<PricebookBundleWithUsage[] | undefined> {
    return this.cache.observe$<PricebookBundleWithUsage[] | undefined>('teamPricebooksInUse');
  }
}

export class GroupAvailablePricebooksSubject implements ISubject<AvailablePricebooks | undefined> {
  private readonly cache: DataCache<PricebookCompartments>;

  constructor(@inject(ScriniumServices.AppStorage) private readonly storage: IAppStorage) {
    this.cache = this.storage.retrieve<PricebookCompartments>(pricebookStorageKey);
  }

  createObservable(): Observable<AvailablePricebooks | undefined> {
    return this.cache.observe$<AvailablePricebooks | undefined>('groupAvailablePricebooks');
  }
}

export class AllPricebookBundlesSubject implements ISubject<PricebookBundle[] | undefined> {
  private readonly cache: DataCache<PricebookCompartments>;

  constructor(@inject(ScriniumServices.AppStorage) private readonly storage: IAppStorage) {
    this.cache = this.storage.retrieve<PricebookCompartments>(pricebookStorageKey);
  }

  createObservable(): Observable<PricebookBundle[] | undefined> {
    return this.cache.observe$<PricebookBundle[] | undefined>('allPricebookBundles');
  }
}
